import { media } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { ButtonClickedEvent, useAnalyticsEvents } from '@atlassian/mpac-analytics';
import { BodyLarge, BundleCard, FluidContainer, H3 } from '@atlassian/mpac-primitives';
import { css, styled } from '@compiled/react';

import CollabFlowBundleImage from '../assets/collab_flow_bundle_tile.png';
import DevOpsBundleImage from '../assets/devops_bundle_tile.png';
import QABundleImage from '../assets/qa_bundle_tile.png';
import SupportFlowBundleImage from '../assets/support_flow_bundle_tile.png';

const BundleSectionStyle = css({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  paddingTop: token('space.800'),
  paddingBottom: token('space.800'),
  marginBottom: token('space.800'),
});

const SectionHeaderStyle = css({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  marginBottom: token('space.400'),
  position: 'relative',
  textAlign: 'center',
  [media.only.sm]: {
    marginBottom: token('space.300'),
  },
});

const H3Style = css({
  fontWeight: '700',
  textAlign: 'center',
});

const DescriptionStyle = css({
  marginTop: token('space.100'),
  textAlign: 'center',
  maxWidth: '41.8rem',
});

const CardsContainerWrapper = styled.div({
  width: '100%',
  position: 'relative',
  overflow: 'hidden',
});

const CardsContainer = styled.div({
  display: 'flex',
  flexDirection: 'row',
  gap: token('space.400'),
  overflowX: 'auto',
  padding: `${token('space.100')} ${token('space.300')} ${token('space.400')}`,
  marginLeft: token('space.negative.300'),
  width: 'calc(100% + 40px)',
  scrollbarWidth: 'none',
  '-ms-overflow-style': 'none',
  '&::-webkit-scrollbar': {
    display: 'none',
  },
  // Add padding to ensure the last card is fully visible when scrolling
  '&::after': {
    content: '""',
    flex: '0 0 16px',
  },

  [media.above.md]: {
    overflowX: 'visible',
    flexWrap: 'nowrap',
    justifyContent: 'center',
    padding: `${token('space.100')} 0 ${token('space.400')}`,
    marginLeft: 0,
  },
});

const bundles = [
  {
    id: 'devops',
    title: 'DevOps Flow Essentials',
    category: 'DEVOPS',
    image: DevOpsBundleImage,
  },
  {
    id: 'devopsEssentials',
    title: 'DevOps Flow Essentials',
    category: 'DEVOPS',
    image: DevOpsBundleImage,
  },
  {
    id: 'collaboration',
    title: 'Collaboration Flow Optimizer',
    category: 'COLLABORATION',
    image: CollabFlowBundleImage,
  },
  {
    id: 'serviceManagement',
    title: 'Support Flow Optimizer',
    category: 'SERVICE MANAGEMENT',
    image: SupportFlowBundleImage,
  },
  {
    id: 'testManagement',
    title: 'QA Track Power Suite',
    category: 'TEST MANAGEMENT',
    image: QABundleImage,
  },
];

export const BundleSection = ({ bundleId }: { bundleId: string }) => {
  const fireEvent = useAnalyticsEvents();

  const trackBundleCardClick = () => {
    fireEvent({
      eventType: ButtonClickedEvent,
      actionSubjectId: 'bundleCardClick',
      attributes: {
        bundleId,
        source: 'bundlePage',
      },
    });
  };

  return (
    <div css={BundleSectionStyle}>
      <FluidContainer>
        <div css={SectionHeaderStyle}>
          <div>
            <H3 as="h2" css={H3Style}>
              More solutions, bundled for you
            </H3>
            <BodyLarge css={DescriptionStyle}>
              Browse our range of app bundles to find the right tools for your team.
            </BodyLarge>
          </div>
        </div>
        <CardsContainerWrapper>
          <CardsContainer>
            {bundles
              .filter((bundle) => bundle.id !== 'devopsEssentials' && bundle.id !== bundleId)
              .map((bundle) => (
                <BundleCard
                  key={bundle.id}
                  title={bundle.title}
                  category={bundle.category}
                  image={bundle.image}
                  navigationUrl={`/bundles/${bundle.id}`}
                  onClick={() => trackBundleCardClick()}
                />
              ))}
          </CardsContainer>
        </CardsContainerWrapper>
      </FluidContainer>
    </div>
  );
};
